// De-anonymized admin bid-ledger payload + mapper for the obscured admin
// channel `admin:auction:{id}:bids:ledger:{token}` (ADR 042 §3, BL-1253).
//
// This is the CONFIDENTIAL, clerk-only sibling of `bid-ledger.ts`. The public
// `bid-ledger.ts` payload (`PublicBidLedgerEntry`) rides the anon-subscribable
// `lot:{id}:bids:ledger` topic (its `auction:{id}:bids:ledger` sibling was
// deleted in BL-1632 — zero subscribers) and carries ONLY
// the opaque `bidderHash` + "Bidder #42" alias — a buyer must never see another
// buyer's real name. The clerk running the auction needs the OPPOSITE: real
// identity (name + paddle number) to manage edits/withdrawals and read "whose
// bid is high".
//
// So this payload carries the bidder's real `customers.id` — the JOIN KEY —
// and it rides ONLY the obscured admin channel (mirrors BL-1252's
// admin-lot-state confidentiality). It MUST NOT appear on any bidder/public
// ledger, snapshot, or REST payload (RAC 1). The de-anon feed is strictly
// ADDITIVE: the anon `PublicBidLedgerEntry` broadcast is unchanged.
//
// Like the public ledger, each broadcast event is one appended (or amended, per
// BL-547) bid; the client's `LedgerContext` handles ordering + dedupe by
// `(createdAt, id)`. The feed is AUCTION-WIDE (one topic per auction) — the
// clerk client filters to a single lot client-side (each entry carries `lotId`),
// which also delivers the cross-lot feed for free (AC 3).
//
// Rule #32 + BL-1014: every field here is realtime-only on a SUBSCRIBING admin
// surface. There is no REST/SSR twin — the catch-up route is the exclusive
// authenticated seed path, and it mints the obscured topic; nothing else
// carries these values to the client.
//
// WHAT THIS PAYLOAD DELIBERATELY DOES NOT CARRY (1): the bidder's NAME and
// PADDLE NUMBER. BL-1253 created this channel to de-anonymize and put both here,
// nine days before BL-1325 articulated the rule that identity stays off the
// wire; BUG-1427 brought this channel into line with its two siblings (the
// pre-bid delta, BL-1325, and the registrations delta, BL-1262), which have
// always refused to carry it. Name and paddle are per-(bidder, auction) facts
// and these are per-bid events, so denormalizing them needed an auction-wide
// re-publish per registration mutation to stay fresh — and when that misfired
// it failed PER ROW, showing one person under two paddle numbers in the pane a
// clerk reads back during a dispute (BUG-1324). The console joins both from
// `useAuctionBidders` on `bidderCustomerId` instead, which fails uniformly and
// self-consistently if it is stale at all. Identity is treated as STATIC: a
// paddle reissued mid-sale from ANOTHER session lands on the next roster
// re-read (the registrations stream already triggers one), and a clerk who
// reissues from this console sees it immediately. Do not add them back here.
//
// WHAT THIS PAYLOAD DELIBERATELY DOES NOT CARRY (2): a bidder's standing ceiling.
// BL-1447 put `standingMaxAmount` / `previousMaxAmount` here and BUG-1426 took
// them back out. A ceiling is a MUTABLE per-(lot, bidder) fact; this payload
// models IMMUTABLE per-bid `bids` rows. Denormalizing the first onto the second
// meant every `max_bids` mutation had to re-publish that bidder's entries
// auction-wide — measured at 31 broadcasts for ONE ceiling change, exactly one
// of them on the lot that changed — via a trigger with no structural link back
// to `max_bids`, so it had to be hand-wired per app and was twice forgotten
// (BUG-1324, BUG-1423). The ceiling rides the admin max-bid delta stream, whose
// current-state LWW shape matches how `max_bids` actually behaves, and the
// console folds it with `selectStandingMaxLadder`. Do not add it back here.

import type Decimal from 'decimal.js';

import type { MoneyWire } from '../../isomorphic/money/codec';
import type { Money } from '../../isomorphic/money/money';
import type { LedgerItem } from '../../stores/realtime/ledger-store';
import type { LedgerCursor } from '../../stores/realtime/ledger-store';
import { isBidLedgerStatus, type BidLedgerStatus } from './bid-ledger';
import { decodeWireAmount, encodeRowMoney } from './money-wire-amount';

/**
 * De-anonymized shape of a single bid as it travels on the obscured admin
 * ledger channel. Extends `LedgerItem` so the `LedgerStore`'s ordering + dedupe
 * (`id` + `createdAt`) works without any consumer-side adapter — identical
 * transport contract to `PublicBidLedgerEntry`, differing ONLY in the identity
 * fields (real name/number/customer id instead of hash/alias).
 */
export interface AdminBidLedgerEntry extends LedgerItem {
    id: string;
    createdAt: string;
    /** `bids.seq` — the monotonic insert-order key (BUG-1282). The ordering key
     *  for any surface that renders these entries as a list: `createdAt` ties to
     *  the microsecond for rows written in one transaction and `amount` ties in a
     *  max-bid duel, so neither can order the feed. */
    seq: number;
    lotId: string;
    /** The bid amount in its own currency (`bids.amount` + `bids.currency`),
     *  encoded by the same `encodeRowMoney` as the public ledger (BL-1942).
     *  The client decodes it once with {@link decodeAdminBidLedgerEntry}. */
    amount: MoneyWire;
    status: BidLedgerStatus;
    placedByClerk: boolean;
    isAuto: boolean;
    /** CONFIDENTIAL — the bidder's real `customers.id`. Admin channel only. The
     *  clerk client keys on this to link a bid to its bidder record, and joins
     *  the bidder's name + paddle from the authenticated roster on it
     *  (BUG-1427). This is the payload's ONLY identity field. */
    bidderCustomerId: string;
    /** CONFIDENTIAL — `bids.reason`, the operator's note on this bid (BL-1756).
     *  Admin channel only: the public `bid-ledger` mapper never carries it
     *  (ADR 061 — Derived Hammer § 2.6). One column, several writers — a clerk
     *  placement, a private sale or swap, and a withdrawal, which replaces
     *  whatever was there — so it explains the bid's CURRENT state. `null` when
     *  nobody gave a reason. */
    reason: string | null;
}

/**
 * Input row shape — duck-typed to the Drizzle SELECT the publisher + catch-up
 * factory run against `bids`. Listing only the fields the mapper reads keeps
 * callers free to project wider rows without dragging PII through the mapper.
 * Mirrors `BidLedgerEntryRow` in `bid-ledger.ts`.
 */
export interface AdminBidLedgerEntryRow {
    id: string;
    /** `bids.lot_seq` — the per-lot sequence, projected so the mapper carries the
     *  ordering key through. Sourced from `lot_seq`, NOT the global `bids.seq`
     *  (BL-1631). */
    seq: number;
    lotId: string;
    /** `bids.amount` — numeric(18,4) decoded to an exact Decimal by the
     *  moneyNumeric custom type (ADR 060 — Money Type, BL-1917). */
    amount: Decimal;
    /** `bids.currency` — the row's own ISO 4217 code (BL-1807, BL-1942). */
    currency: string;
    status: string;
    createdAt: string;
    placedByClerk: boolean;
    isAuto: boolean;
    /** `bids.customer_id` — the bidder's real `customers.id`. BUG-1427 moved
     *  this onto the row itself: it is a column of the bid, and it was only
     *  routed through a separate identity object because it travelled with the
     *  name and paddle that object also carried. */
    bidderCustomerId: string;
    /** `bids.reason` (BL-1756). */
    reason: string | null;
}

/**
 * Result of the admin bid-ledger catch-up route
 * (`createAdminLotBidLedgerCatchupHandler` — the auction-wide handler this
 * originally named was deleted in BL-1556): the minted obscured `topic`, the
 * lot's bid `items` (de-anon) since the client's cursor, and the max
 * `cursor` in that set (the client's next reconcile cursor, or `null` when the
 * auction has no bids). Mirrors `LotStateCatchupResult`, adapted to the ledger
 * per-lot sequence cursor model (BL-1631; was `(createdAt, id)`).
 */
export interface AdminBidLedgerCatchupResult {
    topic: string;
    items: AdminBidLedgerEntry[];
    cursor: LedgerCursor | null;
}

/**
 * Map a single bid row to the admin ledger payload. Returns `null` for rows with
 * an unknown status — the DB enum prevents this today, but dropping is safer
 * than emitting an item with an invalid status to every clerk subscriber.
 * Mirrors `mapBidLedgerEntryPublic`.
 *
 * Takes the row positionally: BUG-1427 removed the second input (de-anon
 * identity), and the options object existed to keep two named inputs readable at
 * the call sites.
 *
 * @param row - the projected `bids` row.
 * @returns the payload, or `null` for an unrecognized status.
 * @sideEffect none — pure reshape.
 */
export function mapAdminBidLedgerEntry(
    row: AdminBidLedgerEntryRow,
): AdminBidLedgerEntry | null {
    // The status set is shared with the public ledger (`isBidLedgerStatus`).
    if (!isBidLedgerStatus(row.status)) {
        return null;
    }
    return {
        id: row.id,
        createdAt: row.createdAt,
        seq: row.seq,
        lotId: row.lotId,
        amount: encodeRowMoney(row),
        status: row.status,
        placedByClerk: row.placedByClerk,
        isAuto: row.isAuto,
        bidderCustomerId: row.bidderCustomerId,
        reason: row.reason,
    };
}

/**
 * An admin ledger entry as the clerk console holds it: the wire amount decoded
 * into a `Money` once, at the hook boundary, so no component re-parses it or
 * pairs it with a currency from anywhere else (BL-1942, ADR 060 — Money Type).
 */
export type AdminBidLedgerEntryDecoded = Omit<AdminBidLedgerEntry, 'amount'> & {
    amount: Money;
};

/** How one admin ledger entry decoded. */
export type AdminBidLedgerEntryDecodeResult =
    | { kind: 'ok'; entry: AdminBidLedgerEntryDecoded }
    | { kind: 'undecodable' };

/**
 * Decode one admin ledger entry's wire amount (BL-1942).
 *
 * @param entry - The entry as it arrived (broadcast or catch-up).
 * @returns The decoded entry, or `undecodable`. Never throws: a row whose
 *   amount is not a well-formed MoneyWire comes back `undecodable` for the
 *   caller to log and skip.
 */
export function decodeAdminBidLedgerEntry(entry: AdminBidLedgerEntry): AdminBidLedgerEntryDecodeResult {
    const decoded = decodeWireAmount(entry.amount);
    if (decoded.kind !== 'money') return decoded;
    return { kind: 'ok', entry: { ...entry, amount: decoded.money } };
}
